import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';

import {
  useStaffPickerQuery,
  type StaffPickerCandidate,
} from '../hooks/use-staff-picker';

interface PatientRegistrationStaffPickerProps {
  open: boolean;
  // Already-assigned staff (primary + secondaries). Hidden from the list to
  // prevent duplicate adds.
  excludeIds: readonly number[];
  onSelect: (staff: StaffPickerCandidate) => void;
  onClose: () => void;
}

// PRD §3.7 — secondary-picker modal. 560 px wide (medium modal spec from
// prototype). Searches Registered medical staff by name / department / role.
export function PatientRegistrationStaffPicker({
  open,
  excludeIds,
  onSelect,
  onClose,
}: PatientRegistrationStaffPickerProps) {
  const { t } = useTranslation('admin');
  const { data, isLoading } = useStaffPickerQuery();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    if (!data) return [] as StaffPickerCandidate[];
    const excluded = new Set(excludeIds);
    const q = query.trim().toLowerCase();
    return data.filter((s) => {
      if (excluded.has(s.id)) return false;
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        s.department.toLowerCase().includes(q) ||
        s.jobRole.toLowerCase().includes(q)
      );
    });
  }, [data, query, excludeIds]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay modal-overlay--visible"
      style={{ zIndex: 1100 /* above the registration modal */ }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="modal"
        style={{
          maxWidth: 560,
          width: '100%',
          maxHeight: '90vh',
          // See PatientRegistrationModal — the global `.modal` class lacks
          // display:flex / max-height, so the header+body+footer layout has
          // to be set up inline.
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal__header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <h2 className="modal__title">
            {t('patientManagement.registrationModal.staffPicker.title')}
          </h2>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label={t('patientManagement.actions.cancel')}
            style={{
              background: 'none',
              border: 'none',
              padding: 6,
              cursor: 'pointer',
              color: 'var(--muted-foreground)',
            }}
          >
            <X size={18} />
          </button>
        </div>
        <div
          className="modal__body"
          style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 10px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: 'var(--space-3)',
            }}
          >
            <Search size={16} color="var(--muted-foreground)" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(
                'patientManagement.registrationModal.staffPicker.searchPlaceholder',
              )}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 'var(--font-size-sm)',
              }}
              autoFocus
            />
          </div>

          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              maxHeight: 320,
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {isLoading && (
              <li
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--muted-foreground)',
                }}
              >
                {t('patientManagement.registrationModal.staffPicker.loading')}
              </li>
            )}
            {!isLoading && filtered.length === 0 && (
              <li
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--muted-foreground)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {t('patientManagement.registrationModal.staffPicker.empty')}
              </li>
            )}
            {!isLoading &&
              filtered.map((s) => (
                <li
                  key={s.id}
                  onClick={() => onSelect(s)}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <strong>{s.name}</strong>
                    <span
                      style={{
                        fontSize: 10,
                        padding: '1px 5px',
                        borderRadius: 3,
                        background: 'var(--muted)',
                        color: 'var(--muted-foreground)',
                        fontWeight: 600,
                      }}
                    >
                      {s.jobRole}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--muted-foreground)',
                      marginTop: 2,
                    }}
                  >
                    {s.department}
                  </div>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>,
    document.body,
  );
}
